import React, { useState } from 'react';
import { Activity, ArrowLeftRight, Layers, Crosshair, BookOpen, Lightbulb, Shield } from 'lucide-react';

const LESSONS = [
  {
    id: 'gex',
    icon: Activity,
    title: 'What is Gamma Exposure (GEX)?',
    duration: '3 min read',
    accent: '#71717a',
    sections: [
      {
        heading: 'The Simple Version',
        content: `Every time you buy or sell an option, a market maker takes the other side of the trade. To stay neutral, they have to constantly buy and sell shares of the underlying stock as the price moves. This hedging activity is measured by something called Gamma Exposure — or GEX.

GEX tells you how much buying or selling pressure dealers will create at each price level. It's like an X-ray of the market showing you where the invisible walls are.`
      },
      {
        heading: 'Positive vs Negative Gamma',
        content: `When GEX is POSITIVE, dealers are "long gamma." This means:
• If price goes up → dealers sell shares (pushing price back down)
• If price goes down → dealers buy shares (pushing price back up)
• Result: Price gets trapped in a range. Low volatility. Mean-reverting.

When GEX is NEGATIVE, dealers are "short gamma." This means:
• If price goes up → dealers buy more shares (adding fuel to the rally)
• If price goes down → dealers sell more shares (accelerating the drop)
• Result: Explosive moves. High volatility. Trending.`
      },
      {
        heading: 'Why This Matters to You',
        content: `Knowing whether dealers are suppressing or amplifying moves tells you what kind of market you're trading in BEFORE you enter a trade. In positive gamma, fading moves (buying dips, selling rips) works well. In negative gamma, riding momentum and using wider stops is smarter.

Most retail traders trade the same way regardless of the regime — and that's why they lose money when conditions change.`
      }
    ]
  },
  {
    id: 'dealers',
    icon: ArrowLeftRight,
    title: 'How Dealers Move the Market',
    duration: '4 min read',
    accent: '#71717a',
    sections: [
      {
        heading: 'Who Are Market Makers?',
        content: `Market makers (also called "dealers") are firms like Citadel Securities, Virtu Financial, and Susquehanna. When you buy a call option on SPY, a dealer sells it to you. They don't want to bet on direction — they just want to collect the bid-ask spread.

But here's the catch: selling you that call option exposes them to risk. If SPY goes up, they lose money on the call they sold you. So they hedge by buying shares of SPY.`
      },
      {
        heading: 'The Hedging Machine',
        content: `Here's where it gets interesting. As SPY moves, the amount of shares dealers need to hold changes constantly. This is driven by "gamma" — the rate at which their hedge ratio changes.

Think of it like a thermostat. If the room (price) gets too hot (goes up), the AC (dealer selling) kicks in harder. If it gets too cold (goes down), the heater (dealer buying) ramps up.

This creates REAL buying and selling pressure — not some chart pattern or indicator. These are actual orders hitting the market worth millions of dollars.`
      },
      {
        heading: 'The Trillion-Dollar Flywheel',
        content: `The options market in the US is enormous — trillions of dollars in notional value. When a huge amount of open interest sits at a specific strike (say SPY $600), dealers are forced to hedge around that level.

If SPY approaches $600 with massive call open interest, dealers start selling shares aggressively to stay neutral. This creates a ceiling — the "Call Wall." The same happens in reverse at puts — creating a floor.

This isn't theory. It's mechanics. And it happens every single trading day.`
      }
    ]
  },
  {
    id: 'levels',
    icon: Layers,
    title: 'How to Read Call Wall / Put Wall / Gamma Flip',
    duration: '5 min read',
    accent: '#71717a',
    sections: [
      {
        heading: 'Call Wall — The Ceiling',
        content: `The Call Wall is the strike price with the highest positive GEX from call options. At this level, dealers hold massive hedges and will sell aggressively if price approaches.

How to use it:
• Treat it as resistance. Price often reverses or stalls here.
• If price breaks ABOVE the Call Wall, expect the wall to shift higher — dealers reposition.
• Selling calls or taking profits near the Call Wall is a high-probability play.`
      },
      {
        heading: 'Put Wall — The Floor',
        content: `The Put Wall is the strike with the highest negative GEX from put options. Dealers will buy shares aggressively near this level to hedge their put exposure.

How to use it:
• Treat it as support. Price bounces off this level frequently.
• If price breaks BELOW the Put Wall, the next put level becomes the new floor — and the move can accelerate.
• Buying near the Put Wall with a stop just below is a classic GEX-based entry.`
      },
      {
        heading: 'Gamma Flip — The Pivot',
        content: `The Gamma Flip is the price where net GEX switches from positive to negative. It's the most important level on the board.

Above Gamma Flip: Positive gamma territory. Dealers stabilize. Range-bound.
Below Gamma Flip: Negative gamma territory. Dealers amplify. Volatile.

How to use it:
• Above the flip → trade mean-reversion strategies (fade extremes)
• Below the flip → trade momentum strategies (follow the trend)
• The flip itself often acts as a magnet — price frequently returns to it.`
      },
      {
        heading: 'Putting It All Together',
        content: `Here's a typical setup:

SPY is at $680. Call Wall is at $700. Put Wall is at $675. Gamma Flip is at $684.

Since SPY ($680) is below the Gamma Flip ($684), we're in negative gamma — dealers are amplifying moves. The Put Wall at $675 is only $5 away, so there's support there.

Trade idea: Watch for a bounce off $675 (Put Wall). If it holds, buy with a target of $684 (Gamma Flip). If $675 breaks, step aside — dealers will accelerate the move down.

This is how institutional traders think. Now you can too.`
      }
    ]
  },
  {
    id: 'trading',
    icon: Crosshair,
    title: 'How to Use GEX for Trading Decisions',
    duration: '5 min read',
    accent: '#71717a',
    sections: [
      {
        heading: 'Rule #1: Know the Regime',
        content: `Before you trade anything, check the GEX positioning:

POSITIVE GAMMA (green) → Range day likely
• Fade moves to extremes
• Sell options (premium sellers win)
• Tighter stops, smaller targets
• Buy near Put Wall, sell near Call Wall

NEGATIVE GAMMA (red) → Trending day likely
• Follow momentum, don't fade
• Buy options (premium buyers win)
• Wider stops, bigger targets
• Breakout trades work better`
      },
      {
        heading: 'Rule #2: Respect the Walls',
        content: `Call Wall and Put Wall are not just lines — they're levels where real money is positioned.

Do:
• Take profits near the Call Wall (longs) or Put Wall (shorts)
• Enter trades bouncing off these levels
• Use them as stop-loss references

Don't:
• Blindly short at the Call Wall without confirmation
• Assume walls never break — they do, especially in negative gamma
• Ignore the walls just because your chart pattern says something different`
      },
      {
        heading: 'Rule #3: Watch for Regime Changes',
        content: `The most powerful trades happen when the market transitions from one gamma regime to another.

Price dropping through the Gamma Flip from above → The market just went from stable to volatile. Expect acceleration. Get with the trend or get out.

Price rising through the Gamma Flip from below → Volatility is about to compress. The range is about to tighten. Switch from momentum to mean-reversion.

These transitions often happen around 10:00-10:30 AM ET when the market establishes its daily range.`
      },
      {
        heading: 'Rule #4: Combine with Your Existing Strategy',
        content: `GEX doesn't replace your trading strategy — it enhances it.

If you trade technical analysis:
• Use Call Wall / Put Wall as confirmation of support/resistance
• Trust your setups more when they align with GEX levels
• Avoid fighting GEX — if your setup says buy but you're pushing into the Call Wall, be cautious

If you trade options:
• Sell premium in positive gamma environments (theta works better when range is tight)
• Buy premium in negative gamma environments (moves are bigger)
• Use Put Wall and Call Wall as strike selection guides

If you day trade:
• Check GEX every morning before the open
• Know the regime before placing any trade
• Use Gamma Flip as your daily bias level`
      },
      {
        heading: '⚠️ What GEX Cannot Do',
        content: `Be honest with yourself about limitations:

• GEX does NOT predict direction — it predicts the type of move
• GEX levels shift daily as options are opened and closed
• Major news events (FOMC, CPI, earnings) can override all GEX levels
• GEX works best on indices (SPY, QQQ) and mega-caps — less reliable on small/mid-caps with low options volume

GammaLens is a tool in your toolbox, not a crystal ball. The edge comes from combining GEX with your own analysis, risk management, and discipline.`
      }
    ]
  },
  {
    id: 'trapzone',
    icon: Shield,
    title: 'Trap Zone',
    duration: '4 min read',
    accent: '#71717a',
    sections: [
      {
        heading: 'Trap Zone',
        content: `The Trap Zone is a visualization of where dealer gamma exposure is most concentrated on SPY and QQQ. It identifies a price band between the call wall and put wall where a high percentage of total dealer gamma sits.`
      },
      {
        heading: 'What dealer hedging does inside the band',
        content: `When a large share of dealer gamma is concentrated in a narrow price range, dealers are mechanically required to hedge against price movement inside that range. As price moves up, hedging flows lean against the move. As price moves down, hedging flows also lean against the move. This is a function of how delta hedging works — it is not a forecast.`
      },
      {
        heading: 'What the data shows',
        content: `• Compression % — the share of total dealer gamma concentrated within the band. Higher values mean gamma is more tightly packed.

• Band width — the dollar distance between the upper and lower edges of the band.

• Bias — whether put-side or call-side gamma dominates the band.

• Active state — fires only when both compression and band width cross internal thresholds. Inactive does not mean "no signal" — it means the conditions for a tight, concentrated band are not currently met.`
      },
      {
        heading: 'Why it\'s limited to SPY and QQQ',
        content: `Dealer positioning is one of several flows that move price. On SPY and QQQ, options-driven flow is large enough relative to other flows that dealer gamma is observable in price behavior. On individual stocks, fundamental flow, news, and single-name catalysts typically dominate, which is why the Trap Zone calculation is restricted to these two tickers.`
      },
      {
        heading: '⚠️ Important',
        content: `The Trap Zone is a description of current dealer positioning. It is not a prediction, not a trade signal, and not a recommendation. Market behavior is influenced by many factors — news, macro events, earnings, and order flow outside the options market — that can override dealer hedging at any time. This information is provided for educational purposes only and is not financial advice. Always do your own research and consult a licensed financial advisor before making any trading decisions.`
      }
    ]
  }
];

export default function LearnTab() {
  const [activeLessonId, setActiveLessonId] = useState(null);

  const activeLesson = LESSONS.find(l => l.id === activeLessonId);

  return (
    <div>
      {!activeLesson ? (
        /* LESSON LIST */
        <div>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <BookOpen size={20} color="#a78bfa" strokeWidth={1.5} />
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#fff', margin: 0 }}>Learn GEX Trading</h2>
            </div>
            <p style={{ fontSize: '14px', color: '#a1a1aa', margin: 0 }}>
              Everything you need to understand gamma exposure and trade with confidence.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {LESSONS.map((lesson, i) => (
              <button
                key={lesson.id}
                onClick={() => setActiveLessonId(lesson.id)}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.09)',
                  borderRadius: '14px',
                  padding: '22px 24px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '18px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
              >
                <div style={{
                  width: '52px', height: '52px', borderRadius: '14px',
                  background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <lesson.icon size={22} color="#a78bfa" strokeWidth={1.5} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '10px', color: '#a78bfa', fontWeight: '700', letterSpacing: '1px' }}>
                      LESSON {i + 1}
                    </span>
                    <span style={{ fontSize: '10px', color: '#475569' }}>•</span>
                    <span style={{ fontSize: '10px', color: '#475569' }}>{lesson.duration}</span>
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 4px 0' }}>{lesson.title}</h3>
                  <p style={{ fontSize: '13px', color: '#a1a1aa', margin: 0 }}>{lesson.sections.length} sections</p>
                </div>
                <span style={{ color: '#475569', fontSize: '18px' }}>→</span>
              </button>
            ))}
          </div>

          {/* Quick tip */}
          <div style={{
            marginTop: '24px', padding: '18px 20px',
            background: 'rgba(234,179,8,0.04)', border: '1px solid rgba(234,179,8,0.1)',
            borderRadius: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Lightbulb size={14} color="#fbbf24" strokeWidth={1.5} />
              <span style={{ fontSize: '12px', color: '#fbbf24', fontWeight: '700' }}>PRO TIP</span>
            </div>
            <p style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: 1.7, margin: 0 }}>
              Start with Lesson 1 and 2 to understand the basics. Then check the GEX Analysis tab for SPY and try to identify the Call Wall, Put Wall, and Gamma Flip yourself before reading the AI interpretation. This builds your intuition faster than anything.
            </p>
          </div>
        </div>
      ) : (
        /* LESSON DETAIL */
        <div>
          <button
            onClick={() => setActiveLessonId(null)}
            style={{
              background: 'none', border: 'none', color: '#a1a1aa',
              fontSize: '13px', cursor: 'pointer', marginBottom: '18px',
              padding: 0,
            }}
          >
            ← Back to all lessons
          </button>

          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: '14px',
            padding: '32px 28px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '12px',
                background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <activeLesson.icon size={20} color="#a78bfa" strokeWidth={1.5} />
              </div>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#fff', margin: '0 0 4px 0' }}>
                  {activeLesson.title}
                </h2>
                <span style={{ fontSize: '12px', color: '#475569' }}>{activeLesson.duration}</span>
              </div>
            </div>

            {activeLesson.sections.map((section, i) => (
              <div key={i} style={{ marginBottom: i < activeLesson.sections.length - 1 ? '28px' : 0 }}>
                <h3 style={{
                  fontSize: '16px', fontWeight: '700', color: '#a78bfa',
                  margin: '0 0 12px 0',
                  paddingBottom: '8px',
                  borderBottom: '1px solid rgba(167,139,250,0.10)',
                }}>
                  {section.heading}
                </h3>
                <div style={{
                  fontSize: '14px', color: '#cbd5e1', lineHeight: 1.85,
                  whiteSpace: 'pre-line',
                }}>
                  {section.content}
                </div>
              </div>
            ))}
          </div>

          {/* Next lesson */}
          {(() => {
            const currentIndex = LESSONS.findIndex(l => l.id === activeLessonId);
            const nextLesson = LESSONS[currentIndex + 1];
            if (!nextLesson) return null;
            return (
              <button
                onClick={() => { setActiveLessonId(nextLesson.id); window.scrollTo(0, 0); }}
                style={{
                  marginTop: '16px', width: '100%', padding: '16px 20px',
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '12px', cursor: 'pointer', textAlign: 'left',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(167,139,250,0.25)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}
              >
                <div>
                  <span style={{ fontSize: '10px', color: '#475569', letterSpacing: '1px' }}>NEXT LESSON</span>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#f1f5f9', margin: '4px 0 0 0' }}>{nextLesson.title}</h4>
                </div>
                <span style={{ color: '#a78bfa', fontSize: '18px' }}>→</span>
              </button>
            );
          })()}
        </div>
      )}
    </div>
  );
}
